import { useWindowSize } from '@/common/hooks/useWindowSize';
import useAccountStore from '@/user/stores/accountStore';
import { Box, styled, useMediaQuery, useTheme } from '@mui/material';
import { useTranslation } from 'next-i18next';
import { FC, ReactNode, useEffect, useRef, useState } from 'react';
import { DaysLeftAlert } from './components/DaysLeftAlert/DaysLeftAlert';
import Header from './components/Header';
import InferiorMenu from './components/InferiorMenu/InferiorMenu';
import Sidebar from './components/Sidebar';
import { useToggle } from '@/common/hooks/useToggle';

interface CreativeStudioLayoutProps {
  children?: ReactNode;
}

const LayoutContainer = styled(Box)(({ theme }) => ({
  display: 'grid',
  gridTemplateColumns: `${theme.sidebar.width} calc(100vw - ${theme.sidebar.width})`,
  gridTemplateRows: '100vh',
  gap: '0px 0px',
  gridTemplateAreas: "'Sidebar Content'",
  position: 'relative',

  [theme.breakpoints.down('lg')]: {
    display: 'grid',
    gridTemplateColumns: '100vw',
    gridTemplateRows: '72px calc(100svh - 72px)',
    gap: '0px 0px',
    gridTemplateAreas: "'Header' 'Content'"
  }
}));

const CreativeStudioLayout: FC<CreativeStudioLayoutProps> = ({ children }) => {
  const divRef = useRef<HTMLDivElement>(null);
  const theme = useTheme();
  const { t } = useTranslation();
  const isDesktop = useMediaQuery(theme.breakpoints.up('lg'));
  const { on: isAlertOpen, toggle: onCloseAlert } = useToggle(true);
  const { status, daysTrialEndNumber } = useAccountStore();
  const { height: alertHeight } = useWindowSize(divRef.current);
  const [alertIsRendered, setAlertIsRendered] = useState<boolean>(null);
  const [paddingTopContainer, setPaddingTopContainer] = useState<number>(2.7);

  useEffect(() => {
    if (alertHeight && alertIsRendered && isAlertOpen) {
      setPaddingTopContainer(alertHeight / 9 + 2.7);
    }
  }, [alertHeight, alertIsRendered, isAlertOpen]);

  const ALERT_MESSAGES = {
    TRIALING: `${t('You have only')} ${daysTrialEndNumber} ${t(
      "days left in the trial period. Let's continue creating together! "
    )}`,
    INACTIVE: t(
      'To maintain access to your data and continue using Creative Studio, please subscribe again'
    ),
    CANCELED: t(
      'To maintain access to your data and continue using Creative Studio, please subscribe again'
    ),
    PAST_DUE: t(
      'The payment could not be processed. Please update your payment method, and feel free to contact us if the issue persists.'
    )
  };

  return (
    <LayoutContainer>
      {!isDesktop && <Header />}
      <Sidebar />
      <InferiorMenu />
      <Box
        position="relative"
        gridArea="Content"
        p={theme.spacing(2.7)}
        pt={theme.spacing(paddingTopContainer)}
        overflow="auto"
      >
        <>
          {status !== 'ACTIVE' && (
            <DaysLeftAlert
              message={ALERT_MESSAGES[status]}
              isDesktop={isDesktop}
              ref={divRef}
              onLoad={setAlertIsRendered}
              open={isAlertOpen}
              onClose={() => {
                onCloseAlert();
                setPaddingTopContainer(2.7);
              }}
            />
          )}
          {children}
        </>
      </Box>
    </LayoutContainer>
  );
};

export default CreativeStudioLayout;
